import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PreviewWall from '../components/PreviewWall'
import {
  CODES, MEETING_PREVIEW, SOON_MS, FLASH_MS,
  fmtPrice, fmtTime, fmtCountdown, fmtAgo, splitName,
  RunnerMark, BookLogo, BookLink, CodeIcon, Segmented, BookFilter, NextToGo, RacingSlip, FlucLine, FlucMove, MoverMark,
  buildRunners, racingLeg, useRacingBoard, previewBlur, PREVIEW_CLEAR_ROWS, PREVIEW_CLEAR_BOOKS,
} from './racingShared'
import FlucsModal from './FlucsModal'
import HoverTip from './HoverTip'

// Desktop layout. Data, settings and shared parts: racingShared.jsx.

// The race table sizes itself to the screen: rows share the height left below
// the header, bookmaker columns share the width left after the pinned columns.
const ROW_MIN = 42
const ROW_MAX = 50
const BOOK_MIN = 56
const BOOK_MAX = 76
const PINNED_W = 362      // mark + runner + flucs + best (RacingOdds.css .rc-c-*)
const FLUCS_W = 100       // .rc-c-fluc, taken off PINNED_W while the column is hidden
const TABLE_CHROME_H = 46 + 44 + 48 + 24   // thead + Market % row + scratched line + page bottom
const TIP_MOVES = 5       // prices listed under the chart when hovering a price
const TIP_CHART_W = 228
const TIP_CHART_H = 64

export default function RacingOdds({ isVisible, isPreview: previewProp }) {
  const {
    isPreview, hadTrial, status, loading, slip,
    code, changeCode, market, setMarket, sortByPrice, setSortByPrice,
    selectedId, setSelectedId, selected, now,
    hiddenBooks, changeHiddenBooks, bookOrder, changeBookOrder, orderBooks, allBooks, moveBook,
    railHidden, toggleRail, flucsHidden, toggleFlucs, openMeetings, toggleMeeting,
    flashes, flucs, upcoming, ahead, meetings,
  } = useRacingBoard({ isVisible, isPreview: previewProp, withFlucs: true })

  return (
    <div className="rc-page">
      <header className="rc-head">
        <div className="rc-title-wrap">
          <span className="rc-title-icon" aria-hidden="true">
            <CodeIcon code="thoroughbred" />
          </span>
          <h1 className="rc-title">Racing Odds</h1>
        </div>
        <div className="rc-controls">
          <Segmented label="Racing code" options={CODES} value={code} onChange={changeCode} icons />
          <Segmented label="Market" options={[{ id: 'win', label: 'Win' }, { id: 'place', label: 'Place' }]} value={market} onChange={setMarket} />
          <button className={`rc-filter-btn rc-toggle-btn${flucsHidden ? '' : ' is-on'}`} aria-pressed={!flucsHidden} onClick={toggleFlucs} disabled={isPreview}>
            <span className="rc-filter-check" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </span>
            Flucs
          </button>
          <BookFilter books={allBooks} hidden={hiddenBooks} onChange={changeHiddenBooks} customOrder={bookOrder.length > 0} onResetOrder={() => changeBookOrder([])} onReorder={changeBookOrder} />
        </div>
      </header>
      <NextToGo races={ahead} now={now} selectedId={selectedId} onSelect={setSelectedId} />

      {status === 'reconnecting' && <div className="rc-notice">Reconnecting to the odds feed…</div>}

      {status === 'too_many_tabs' ? (
        <div className="rc-empty">Racing Odds is already open in 2 other tabs or devices. Close one, then reload this page.</div>
      ) : status === 'no_access' ? (
        <div className="rc-empty">Your plan doesn't include Racing Odds. It comes with Promo Premium and Full Access.</div>
      ) : (
        <>
          {/* The rail only hides while a race is open, so it can always come back */}
          <div className={`rc-body${railHidden && selected ? ' is-rail-hidden' : ''}`}>
            <aside className="rc-rail" aria-label="Races" aria-hidden={railHidden && selected ? true : undefined}>
              {meetings.length === 0 && (loading ? <RailSkeleton /> : <div className="rc-rail-empty">No upcoming races</div>)}
              {meetings.length > 0 && <h3 className="rc-rail-label">Meetings</h3>}
              {meetings.map((list) => {
                const key = `${list[0].track}|${list[0].country}`
                const open = openMeetings.has(key)
                const extra = list.length - MEETING_PREVIEW
                // Collapsed: the next 3, plus the open race if it sits further down.
                const shown = open ? list : list.filter((r, i) => i < MEETING_PREVIEW || r.race_id === selectedId)
                return (
                  <div key={key} className="rc-meeting">
                    <button className="rc-meeting-head" onClick={() => extra > 0 && toggleMeeting(key)} disabled={extra <= 0} aria-expanded={extra > 0 ? open : undefined}>
                      <span className="rc-meeting-name">{list[0].track}</span>
                      {list[0].country !== 'AU' && <span className="rc-meeting-country">{list[0].country}</span>}
                      {extra > 0 && (
                        <span className="rc-meeting-more">
                          {open ? 'Less' : `+${extra}`}
                          <svg className={open ? 'is-open' : undefined} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                        </span>
                      )}
                    </button>
                    {shown.map((r) => (
                      <button
                        key={r.race_id}
                        className={['rc-race-row', r.race_id === selectedId && 'is-selected', r.start - now < SOON_MS && 'is-soon', r.abandoned && 'is-abandoned'].filter(Boolean).join(' ')}
                        onClick={() => setSelectedId(r.race_id)}
                      >
                        <span className="rc-race-row-num">R{r.race_number}</span>
                        <span className="rc-race-row-time">{fmtTime(r.start)}</span>
                        <span className="rc-race-row-cd">{r.abandoned ? 'Abandoned' : fmtCountdown(r.start - now)}</span>
                      </button>
                    ))}
                  </div>
                )
              })}
            </aside>

            <main className="rc-main">
              {selected ? (
                <RaceBoard
                  race={selected}
                  code={code}
                  market={market}
                  now={now}
                  hiddenBooks={hiddenBooks}
                  sortByPrice={sortByPrice}
                  onToggleSort={() => setSortByPrice((s) => !s)}
                  flashes={flashes}
                  flucs={flucs}
                  showFlucs={!flucsHidden}
                  siblings={upcoming.filter((r) => r.track === selected.track && r.country === selected.country)}
                  nextAhead={ahead}
                  onSelect={setSelectedId}
                  railHidden={railHidden}
                  onToggleRail={toggleRail}
                  orderBooks={orderBooks}
                  onMoveBook={moveBook}
                  slip={slip}
                  isPreview={isPreview}
                />
              ) : loading || ahead.length > 0 ? (
                <BoardSkeleton />
              ) : (
                <div className="rc-empty">No upcoming races right now. New races appear about 24 hours before they start.</div>
              )}

              {isPreview && (
                <div className="rc-wall">
                  <PreviewWall
                    title="Every bookmaker's racing prices, live, side by side"
                    features={['19 bookmakers compared on every runner', 'Thoroughbred, harness and greyhound', 'Prices update the moment a bookmaker moves', 'Best price highlighted for every runner']}
                    planName="Promo Premium"
                    accent="#a78bfa"
                    hadTrial={hadTrial}
                  />
                </div>
              )}
            </main>
          </div>
        </>
      )}
      <RacingSlip slip={slip} />
      <HoverTip />
    </div>
  )
}

// ---------- loading skeletons ----------

function RailSkeleton() {
  return (
    <div className="rc-skel-rail" aria-label="Loading races">
      {[0, 1, 2, 3].map((m) => (
        <div key={m} className="rc-skel-meeting">
          <span className="rc-skel" style={{ width: `${55 + (m % 2) * 20}%`, height: 11 }} />
          {[0, 1, 2].map((r) => <span key={r} className="rc-skel" style={{ width: '100%', height: 9 }} />)}
        </div>
      ))}
    </div>
  )
}

function BoardSkeleton() {
  return (
    <section className="rc-board" aria-label="Loading race">
      <div className="rc-race-head">
        <span className="rc-skel" style={{ width: 56, height: 26 }} />
        <span className="rc-skel" style={{ width: 190, height: 16 }} />
        <span className="rc-skel" style={{ width: 54, height: 18 }} />
      </div>
      <div className="rc-skel-head">
        {Array.from({ length: 14 }, (_, i) => <span key={i} className="rc-skel" style={{ width: 30, height: 30, borderRadius: 7 }} />)}
      </div>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="rc-skel-row">
          <span className="rc-skel" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <span className="rc-skel" style={{ width: 110 - (i % 3) * 18, height: 12 }} />
          <span className="rc-skel" style={{ width: 62, height: 14 }} />
          <span className="rc-skel-prices">
            {Array.from({ length: 12 }, (_, j) => <span key={j} className="rc-skel" style={{ width: 34, height: 12 }} />)}
          </span>
        </div>
      ))}
    </section>
  )
}

// ---------- the race table ----------

function RaceBoard({ race, code, market, now, hiddenBooks, sortByPrice, onToggleSort, flashes, flucs, showFlucs, siblings, nextAhead, onSelect, railHidden, onToggleRail, orderBooks, onMoveBook, slip, isPreview }) {
  const books = orderBooks(Object.keys(race.books).filter((b) => !hiddenBooks.has(b)))
  const [dragBook, setDragBook] = useState(null)
  const [dropAt, setDropAt] = useState(null)   // { book, side: 'before' | 'after' }
  const endDrag = () => {
    setDragBook(null)
    setDropAt(null)
  }

  const { active, scratched } = buildRunners(race, books, market, sortByPrice, flucs)

  // Clicking a runner's Flucs cell opens its full chart (FlucsModal), by number so it follows live updates.
  const [chartFor, setChartFor] = useState(null)
  useEffect(() => setChartFor(null), [race.race_id])
  const chartRunner = chartFor == null ? null : active.find((r) => r.number === chartFor)

  // Hovering a price with a history shows its last few moves: { book, number, x, y, below }.
  const [tip, setTip] = useState(null)
  const showTip = (e, book, number) => {
    const r = e.currentTarget.getBoundingClientRect()
    const below = r.top < 260
    setTip({ book, number, below, x: Math.max(150, Math.min(window.innerWidth - 150, r.left + r.width / 2)), y: below ? r.bottom + 6 : r.top - 6 })
  }
  useEffect(() => {
    if (!tip) return
    const hide = () => setTip(null)
    window.addEventListener('scroll', hide, true)
    return () => window.removeEventListener('scroll', hide, true)
  }, [tip])

  const wrapRef = useRef(null)
  const [box, setBox] = useState({ width: 0, height: 0 })
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const measure = () => {
      const r = el.getBoundingClientRect()
      // From the table's top as if scrolled to the top of the page, so
      // scrolling never changes the sizes.
      const top = r.top + window.scrollY
      setBox({ width: r.width, height: window.innerHeight - top - TABLE_CHROME_H })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v))
  const rowH = active.length && box.height > 0 ? clamp(Math.floor(box.height / active.length), ROW_MIN, ROW_MAX) : ROW_MIN
  const pinnedW = showFlucs ? PINNED_W : PINNED_W - FLUCS_W
  const bookW = books.length && box.width > 0 ? clamp(Math.floor((box.width - pinnedW) / books.length), BOOK_MIN, BOOK_MAX) : BOOK_MIN

  const marketPct = books.map((b) => {
    if (market !== 'win' || race.abandoned) return null
    const prices = active.map((rn) => race.books[b].runners[rn.number]?.win)
    if (!prices.length || prices.some((p) => p == null)) return null
    return prices.reduce((s, p) => s + 1 / p, 0) * 100
  })

  const sorted = [...siblings].sort((a, b) => a.race_number - b.race_number)
  const idx = sorted.findIndex((r) => r.race_id === race.race_id)
  const prev = sorted[idx - 1]
  const next = sorted[idx + 1]
  // Jumped = the API says the race has closed (2+ books shut betting). Past its
  // start but not closed = overdue: prices stay live, countdown reads "+1:24".
  const jumped = !!race.closed
  const left = race.start - now
  const followOn = jumped
    ? (sorted.find((r) => r.race_number > race.race_number && !r.closed && r.race_id !== race.race_id) ||
       nextAhead.find((r) => r.race_id !== race.race_id))
    : null

  return (
    <section className="rc-board">
      <div className="rc-race-head">
        <button
          className={`rc-rail-toggle${railHidden ? '' : ' is-on'}`}
          onClick={onToggleRail}
          aria-pressed={!railHidden}
          aria-label={railHidden ? 'Show meetings' : 'Hide meetings'}
          data-tip={railHidden ? 'Show meetings' : 'Hide meetings'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="16" rx="2.5" />
            <path d="M9 4v16" />
            <path className="rc-rail-toggle-fill" d="M5.5 7.5h1.5M5.5 10.5h1.5M5.5 13.5h1.5" />
          </svg>
        </button>
        <span className="rc-race-head-sep" />
        <button className="rc-step" disabled={!prev} onClick={() => prev && onSelect(prev.race_id)} aria-label="Previous race">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <button className="rc-step" disabled={!next} onClick={() => next && onSelect(next.race_id)} aria-label="Next race">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
        <h2 className="rc-race-title">{race.track} <span className="rc-race-num">Race {race.race_number}</span></h2>
        <span className="rc-race-time">{fmtTime(race.start)}</span>
        {race.abandoned ? (
          <span className="rc-chip rc-chip-red">Abandoned</span>
        ) : (
          <span className={`rc-chip${jumped ? ' rc-chip-muted' : left < SOON_MS ? ' rc-chip-orange' : ''}`}>{jumped ? 'Jumped' : fmtCountdown(left)}</span>
        )}
        {followOn && (
          <button className="rc-follow" onClick={() => onSelect(followOn.race_id)}>
            Next: {followOn.track} R{followOn.race_number}
          </button>
        )}
      </div>

      <div ref={wrapRef} className={`rc-table-wrap${jumped ? ' is-jumped' : ''}`}>
        <table className={`rc-table${showFlucs ? '' : ' is-no-flucs'}`} style={{ '--rc-row-h': `${rowH}px`, '--rc-book-w': `${bookW}px` }}>
          <thead>
            <tr>
              <th className="rc-c-mark" />
              <th className="rc-c-runner">Runner</th>
              {showFlucs && <th className="rc-c-fluc" data-tip="The runner's price across all bookmakers (the median): opening, previous and now">Flucs</th>}
              <th className="rc-c-best">
                <button className="rc-sort" onClick={onToggleSort} data-tip={sortByPrice ? 'Sort by runner number' : 'Sort by best price'}>
                  Best
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortByPrice ? 1 : 0.4 }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </th>
              {books.map((b) => (
                <th
                  key={b}
                  className={['rc-c-book', 'rc-book-head', race.books[b].status === 'closed' && 'is-closed', dragBook === b && 'is-dragging', dropAt?.book === b && `is-drop-${dropAt.side}`].filter(Boolean).join(' ')}
                  draggable={!isPreview}
                  onDragStart={(e) => {
                    setDragBook(b)
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', b)
                  }}
                  onDragOver={(e) => {
                    if (!dragBook || dragBook === b) return
                    e.preventDefault()
                    const r = e.currentTarget.getBoundingClientRect()
                    const side = e.clientX < r.left + r.width / 2 ? 'before' : 'after'
                    if (dropAt?.book !== b || dropAt?.side !== side) setDropAt({ book: b, side })
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    if (dragBook && dropAt) onMoveBook(dragBook, dropAt.book, dropAt.side)
                    endDrag()
                  }}
                  onDragEnd={endDrag}
                >
                  <BookLink book={b} url={isPreview ? null : race.books[b].url} tip={`${b}${race.books[b].status === 'closed' ? ' (closed)' : ''}. Click to open, drag to move this column`} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {active.map((rn, rowIdx) => {
              // Preview: every book shows, but only the top-left corner of prices is readable.
              return (
                <tr key={rn.number}>
                  <td className="rc-c-mark"><RunnerMark key={`${race.race_id}-${rn.number}`} number={rn.number} silk={rn.silk} code={code} /></td>
                  <td className="rc-c-runner">
                    <div className="rc-runner-name" data-tip={rn.name}>
                      <span className="rc-runner-text">{rn.name}</span>
                      <MoverMark mover={rn.mover} />
                      {rn.emergency && <span className="rc-em">EM</span>}
                    </div>
                    <div className="rc-chance" data-tip={`Market chance ${Math.round(rn.chance * 100)}%`}>
                      <span style={{ width: `${Math.min(100, rn.chance * 100 * 2.2)}%` }} />
                    </div>
                  </td>
                  {showFlucs && <td className="rc-c-fluc">
                    {rn.flucLine?.length > 1 ? (
                      <button className="rc-fluc-open" onClick={() => setChartFor(rn.number)} data-tip="Open the price chart">
                        <span className="rc-fluc">
                          <FlucMove moves={rn.flucLine} />
                          <FlucLine moves={rn.flucLine} now={now} />
                        </span>
                      </button>
                    ) : <span className="rc-dash">–</span>}
                  </td>}
                  <td className={`rc-c-best${isPreview && rowIdx >= PREVIEW_CLEAR_ROWS ? ' is-blurred' : ''}`}>
                    {rn.best != null ? (
                      <div className="rc-best" data-tip={rn.bestBooks.length > 1 ? `Best at ${rn.bestBooks.join(', ')}` : undefined}>
                        <button
                          className={`rc-best-price${slip.has(racingLeg(race, rn.bestBooks[0], rn, market, rn.best)) ? ' is-in-slip' : ''}`}
                          onClick={() => slip.toggle(racingLeg(race, rn.bestBooks[0], rn, market, rn.best))}
                          disabled={isPreview || jumped}
                          data-tip={`Add ${rn.bestBooks[0]} ${fmtPrice(rn.best)} to the betslip`}
                        >
                          {fmtPrice(rn.best)}
                        </button>
                        <span className="rc-best-logo">
                          <BookLink book={rn.bestBooks[0]} url={isPreview ? null : race.books[rn.bestBooks[0]].url} size={27} />
                          {rn.bestBooks.length > 1 && <span className="rc-best-more">+{rn.bestBooks.length - 1}</span>}
                        </span>
                      </div>
                    ) : <span className="rc-dash">–</span>}
                  </td>
                  {books.map((b, i) => {
                    const p = rn.prices[i]
                    const bk = race.books[b]
                    const f = flashes.get(`${race.race_id}|${b}|${rn.number}|${market}`)
                    const bookClosed = bk.status === 'closed'
                    const isBest = p != null && p === rn.best && (!bookClosed || jumped)
                    // A closed book's price is final but can't be bet, so it can't go in the slip.
                    const leg = p != null && !bookClosed && !jumped ? racingLeg(race, b, rn, market, p) : null
                    const inSlip = leg && slip.has(leg)
                    const cls = ['rc-c-book', 'rc-price', isBest && 'is-best', inSlip && 'is-in-slip', bookClosed && 'is-closed', dragBook === b && 'is-dragging', isPreview && previewBlur(rowIdx, i) && 'is-blurred'].filter(Boolean).join(' ')
                    const hasTip = p != null && rn.moves?.[i]?.length > 1
                    return (
                      <td
                        key={b}
                        className={cls}
                        onMouseEnter={hasTip ? (e) => showTip(e, b, rn.number) : undefined}
                        onMouseLeave={hasTip ? () => setTip(null) : undefined}
                      >
                        {f && now - f.at < FLASH_MS && <span key={f.at} className={`rc-flash rc-flash-${f.dir}`} />}
                        {leg && !isPreview
                          ? <button className="rc-price-btn" onClick={() => slip.toggle(leg)} data-tip={hasTip ? undefined : `Add ${b} ${fmtPrice(p)} to the betslip`}>{fmtPrice(p)}</button>
                          : <span>{p == null ? <span className="rc-dash">–</span> : fmtPrice(p)}</span>}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {market === 'win' && !race.abandoned && (
            <tfoot>
              <tr>
                <td className="rc-c-mark" />
                <td className="rc-c-runner rc-foot-label" data-tip="Total of every runner's chance at this bookmaker's prices. Closer to 100% means better value.">Market %</td>
                {showFlucs && <td className="rc-c-fluc" />}
                <td className="rc-c-best" />
                {marketPct.map((pct, i) => (
                  <td key={books[i]} className={`rc-c-book rc-foot${isPreview && i >= PREVIEW_CLEAR_BOOKS ? ' is-blurred' : ''}`}>{pct == null ? <span className="rc-dash">–</span> : `${Math.round(pct)}%`}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {chartRunner && (
        <FlucsModal race={race} runner={chartRunner} books={books} code={code} market={market} now={now} onClose={() => setChartFor(null)} />
      )}
      {tip && <FlucTip tip={tip} active={active} books={books} code={code} now={now} />}

      {scratched.length > 0 && (
        <details className="rc-scratched">
          <summary>Scratched ({scratched.length})</summary>
          <ul>
            {scratched.map((r) => (
              <li key={r.number}><span className="rc-scratched-num">{r.number}</span>{splitName(r.rawName).name}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}


// ---------- flucs ----------

// One bookmaker's price history for a runner: the runner, a line chart of every
// move on a real time axis, then the last few prices left to right (oldest
// first), each coloured by its move. Portalled so the table's scroll box and
// sticky columns can't clip it.
function FlucTip({ tip, active, books, code, now }) {
  const rn = active.find((r) => r.number === tip.number)
  const i = books.indexOf(tip.book)
  const moves = rn?.moves?.[i]
  if (!moves || moves.length < 2) return null
  const prices = moves.map((m) => m.price)
  const first = moves.length - Math.min(TIP_MOVES, moves.length)
  const recent = moves.slice(first)
  return createPortal(
    <div className={`rc-portal rc-fluc-tip${tip.below ? ' is-below' : ''}`} style={{ left: tip.x, top: tip.y }} role="tooltip">
      <div className="rc-fluc-tip-head">
        <RunnerMark number={rn.number} silk={rn.silk} code={code} />
        <div className="rc-fluc-tip-who">
          <span className="rc-fluc-tip-name">{rn.number}. {rn.name}</span>
          <span className="rc-fluc-tip-book"><BookLogo book={tip.book} size={15} />{tip.book}</span>
        </div>
      </div>
      <div className="rc-fluc-tip-chart">
        <FlucLine moves={moves} now={now} width={TIP_CHART_W} height={TIP_CHART_H} detail />
        <span className="rc-fluc-tip-scale">
          <span>{fmtPrice(Math.max(...prices))}</span>
          <span>{fmtPrice(Math.min(...prices))}</span>
        </span>
      </div>
      <div className="rc-fluc-tip-axis">
        <span>Opened {fmtAgo(now - moves[0].t)}</span>
        <span>Now</span>
      </div>
      <ol className="rc-fluc-tip-steps">
        {recent.map((m, k) => {
          const prev = moves[first + k - 1]
          const dir = prev ? (m.price > prev.price ? 'up' : 'down') : null
          return (
            <React.Fragment key={`${m.t}-${m.price}`}>
              {k > 0 && <li className="rc-fluc-tip-sep" aria-hidden="true">›</li>}
              <li className={dir ? `is-${dir}` : undefined}>
                <span className="rc-fluc-tip-price">{fmtPrice(m.price)}</span>
                <span className="rc-fluc-tip-when">{m.live ? 'Now' : fmtAgo(now - m.t).replace(' ago', '')}</span>
              </li>
            </React.Fragment>
          )
        })}
      </ol>
    </div>,
    document.body,
  )
}
